import React, { Component, SyntheticEvent } from 'react';
import { compose } from 'react-recompose';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { push as Push } from 'redux-first-history';
import { withTranslation } from 'react-i18next';

import { Grid, LinearProgress, Link, withStyles } from '@material-ui/core';
import styled from 'styled-components';
import { isEmpty } from 'lodash-es';

import {
    palette,
    Header,
    Radio,
    DropZone,
    SimplePopover,
    TextArea,
    PrimaryButton,
    UploadedFile,
    Container,
    Paper,
    Alert,
    Typography,
} from '@dev/ee-components';
import { ContactStatus, SuppressedStatus } from '@dev/ee-api/ee-api-models';

import { AddSuppressionInterface, StyledMobileType, UploadType } from './AddSuppressionInterface';
import Loader from '@api/components/TransparentLoader';

import {
    AddSuppression as AddSuppressions,
    UploadSuppression,
    CloseSupperssionsScreen,
    ClearContactsPattern,
    DeleteSuppresionsList,
} from '@api/store/suppressions/actions';
import { DeleteUploadedFile } from '@api/store/upload/actions';
import { ApplicationState as State } from '@api/store';
import { ShowToast, HideToast } from '@api/store/toast/actions';
import { ShowToastParams } from '@api/store/toast/types';

import { mimeTypes } from '@api/services/dictionaries/MimeTypes';
import { suppressionStatuses } from '@api/services/dictionaries/ContactStatuses';
import { SuppressionsPaths } from '@api/services/dictionaries/paths';
import WrongFileModal from '../WrongFileModal';
import { isReseller } from '@api/services/Security';
import { links } from '@api/services/dictionaries/links';

type DispatchProps = {
    AddSuppressions(status: string, emails?: string[]): void;
    HideToast(): void;
    ShowToast(params: ShowToastParams): void;
    UploadSuppression(file: File, errorModal: JSX.Element): void;
    DeleteUploadedFile(): void;
    DeleteSuppresionsList(): void;
    Push(url: string): void;
    CloseSupperssionsScreen(): void;
    ClearContactsPattern(): void;
};

type Props = DispatchProps & AddSuppressionInterface;

const dispatchProps = {
    AddSuppressions,
    ShowToast,
    HideToast,
    UploadSuppression,
    DeleteUploadedFile,
    Push,
    DeleteSuppresionsList,
    CloseSupperssionsScreen,
    ClearContactsPattern,
};
const mapState = (state: State, ownProps: AddSuppressionInterface): AddSuppressionInterface => {
    return {
        ...ownProps,
        suppresions: state.suppressions,
        browser: state.browser,
        upload: state.upload,
        account: state.account,
    };
};

const styles = {
    root: {
        padding: '0 16px',
        '& h2': {
            paddingBottom: 8,
        },
        '& .MuiTypography-h1': {
            padding: '16px 15px',
            paddingTop: 0,
        },
        '& .MuiTypography-h2': {
            lineHeight: '24px',
            '& .MuiBox-root': {
                marginBottom: '8px',
            },
        },
        '& .MuiTypography-body1': {
            color: '#677389',
            lineHeight: '20px',
        },
    },

    rootLinkActive: {
        color: palette.gray[4],
        display: 'flex',
        'align-items': 'center',
        '& svg': {
            'margin-left': '10px',
        },
    },
    separator: {
        color: palette.gray[4],
        cursor: 'pointer',
    },
    indicator: {
        backgroundColor: palette.indigo[5],
    },
    li: {
        '& a': {
            display: 'flex',
            'align-items': 'center',
            '& svg': {
                'margin-right': '5px',
            },
        },
        color: palette.indigo[5],
        cursor: 'pointer',
    },
    ol: {
        display: 'flex',
        'align-items': 'center',
        'min-height': '50px;',
    },

    containerRoot: {
        padding: '1.2em',
        background: '#fff',
        'margin-top': '2em',
    },
};

const FileFormatContainer = styled.div<StyledMobileType>`
    width: ${({ isMobile }): number => (isMobile ? 100 : 50)}%;
    margin-bottom: ${({ isMobile }): number => (isMobile ? 6 : 0)}px;
`;

const AlertContainer = styled.div`
    margin: 1em 0;
`;

const ProgressWrapper = styled.div`
    padding: 0.5em 0;
    min-height: 36px;
`;

const StepTitle = styled.div`
    padding-bottom: 28px;
    color: ${palette.jacarta[5]};
    display: flex;
    flex-direction: row;
    align-items: center;
`;

const SingleStep = styled.div`
    margin-top: 24px;
    width: 100%;
`;

const StepContent = styled.div<StyledMobileType>`
    margin: 0 ${({ isMobile }): number => (isMobile ? 0 : 64)}px;
    padding-bottom: 24px;
    border-bottom: 1px solid ${palette.gray[3]};

    textarea {
        margin-bottom: ${({ isMobile }): number => (isMobile ? 6 : 24)}px;
    }
`;

const NumberedItem = styled.span`
    color: ${palette.jacarta[5]};
    font: Bold 16px/24px Nunito;
    letter-spacing: 0px;
    width: 32px;
    height: 32px;
    border-radius: 32px;
    background: #ffffff 0% 0% no-repeat padding-box;
    box-shadow: 0px 1px 3px #67679dcc;
    opacity: 1;
    margin-right: 32px;
    display: inline-block;
    line-height: 32px;
    text-align: center;
`;

const StyledPaper = styled(Paper)<StyledMobileType>`
    padding-top: ${({ isMobile }): number => (isMobile ? 0 : 10)}px;
    padding-bottom: ${({ isMobile }): number => (isMobile ? 16 : 32)}px;
    padding-left: ${({ isMobile }): number => (isMobile ? 20 : 48)}px;
    padding-right: ${({ isMobile }): number => (isMobile ? 20 : 48)}px;
`;

const AddContactGrid = styled(Grid)<StyledMobileType>`
    padding: ${({ isMobile }): string => (isMobile ? '16px 0 0 0' : '1em 64px 0 0')};
`;

class AddSuppression extends Component<Props> {
    public state = {
        uploadType: UploadType.Upload,
        status: ContactStatus.Bounced,
    };

    public componentWillUnmount(): void {
        const { ClearContactsPattern, DeleteUploadedFile, suppresions, upload } = this.props;

        if (suppresions?.contactsPattern) {
            ClearContactsPattern();
        }
        if (upload?.fileuploaded?.FileName) {
            DeleteUploadedFile();
        }
    }

    private textAreaRef = React.createRef<HTMLTextAreaElement>();

    private renderBreadcrumbs(): JSX.Element {
        const { t, Push } = this.props;

        return (
            <Header
                breadcrumbs={{
                    text: t('suppressions'),
                    onClick: (): void => {
                        Push(SuppressionsPaths.Suppressions);
                    },
                }}
                header={{
                    title: t('addcontact'),
                }}
            />
        );
    }

    onFileTypeReject(): void {
        const { ShowToast, t } = this.props;

        ShowToast({
            message: t('wrongfileformat'),
            severity: 'error',
            duration: 4000,
        });
    }

    onFileTypeAccept(files: File[]): void {
        const { UploadSuppression } = this.props;

        UploadSuppression(files[0], <WrongFileModal />);
    }

    removeFile(): void {
        const { ClearContactsPattern, DeleteSuppresionsList, DeleteUploadedFile, suppresions } = this.props;
        DeleteSuppresionsList();
        DeleteUploadedFile();
        if (suppresions?.contactsPattern) {
            ClearContactsPattern();
        }
    }

    private renderUploadStep(): JSX.Element {
        const { upload, t, account } = this.props;
        const { uploadType } = this.state;

        if (uploadType === UploadType.Upload) {
            return (
                <>
                    <AlertContainer>
                        <Alert
                            severity="info"
                            body={
                                <>
                                    {t('uploadcontactsfilesinfo')}
                                    {!isReseller(account) && (
                                        <span>
                                            {`${t('howpreparefile')} `}
                                            {
                                                <Link href={links.contactUpload} rel="noreferrer" target="_blank">
                                                    {t('here')}.
                                                </Link>
                                            }
                                        </span>
                                    )}
                                </>
                            }
                        />
                    </AlertContainer>
                    <ProgressWrapper>
                        {upload.uploading && <LinearProgress variant="determinate" value={upload.progress} />}
                    </ProgressWrapper>
                    {!upload?.fileuploaded?.FileName && (
                        <DropZone
                            acceptMimeType={mimeTypes}
                            onDropRejected={this.onFileTypeReject.bind(this)}
                            onDropAccepted={this.onFileTypeAccept.bind(this)}
                            description=".csv, .zip, .xml, .json"
                            linkText={t('uploadafile')}
                            textAfterLink={t('ordraganddrop')}
                        />
                    )}
                    {upload?.fileuploaded?.FileName && (
                        <UploadedFile filename={upload?.fileuploaded?.FileName} onDelete={this.removeFile.bind(this)} />
                    )}
                </>
            );
        } else {
            return (
                <>
                    <TextArea
                        id="addSuppressionTextArea"
                        name="addSuppressionTextArea"
                        label={t('uploadtypecontacts')}
                        inputRef={this.textAreaRef}
                        placeholder={'email1, email2, ...'}
                        rows={11}
                    />
                </>
            );
        }
    }

    private renderContactStatusSelect(): JSX.Element {
        const { t, browser } = this.props;
        const { status } = this.state;
        const isMobile = browser.lessThan.hd;

        const contactStatuses = suppressionStatuses.map((status): { value: string; label: string } => {
            const { translationKey, enumValue } = status;

            return {
                value: enumValue,
                label: t(translationKey),
            };
        });

        return (
            <FileFormatContainer isMobile={isMobile}>
                <SimplePopover
                    type="input"
                    label={t('setstatusto')}
                    options={contactStatuses}
                    fullWidth={true}
                    value={status}
                    onItemClick={(ev: any, value?: any): void => {
                        this.setState({ status: value.value });
                    }}
                />
            </FileFormatContainer>
        );
    }

    private onUploadChange(ev: SyntheticEvent<HTMLInputElement>, type: UploadType.Upload | UploadType.Manual): void {
        this.setState({ uploadType: type });
    }

    private onAddContactClick(): void {
        const { AddSuppressions, upload, browser } = this.props;
        const isMobile = browser.lessThan.hd;
        const { uploadType, status } = this.state;

        const uploadData = isMobile ? UploadType.Manual : uploadType;

        if (uploadData === UploadType.Manual && !isEmpty(this.textAreaRef.current.value)) {
            const emails = this.textAreaRef.current.value.replace(/\s/g, '').split(/[,;]/);

            const filterdStatus = status === ContactStatus.Abuse ? SuppressedStatus.Complained : status;
            const filteredEmails = emails?.filter(Boolean);
            AddSuppressions(filterdStatus, filteredEmails);
        } else if (uploadData === UploadType.Upload && upload?.fileuploaded?.FileName) {
            AddSuppressions(status);
        }
    }

    private checkIfUploadDisabled(): boolean {
        const { uploadType } = this.state;
        const { suppresions, upload, browser } = this.props;
        const isMobile = browser.lessThan.hd;

        let isUploadDisabled = false;

        if (uploadType === UploadType.Upload && (!suppresions.contactsPattern || !upload?.fileuploaded?.FileName)) {
            isUploadDisabled = true;
        }

        isMobile && (isUploadDisabled = false);

        return isUploadDisabled;
    }

    public render(): JSX.Element {
        const { t, suppresions, browser } = this.props;
        const isMobile = browser.lessThan.hd;

        return (
            <Container maxWidth="sm">
                {suppresions.contactsUploading && <Loader />}
                <Grid container justifyContent={'space-between'} alignContent="center">
                    {this.renderBreadcrumbs()}
                </Grid>
                <StyledPaper isMobile={isMobile}>
                    <Grid item container md={12}>
                        {!isMobile ? (
                            <SingleStep>
                                <StepTitle>
                                    <NumberedItem>1</NumberedItem>
                                    <Typography variant="h2">{t('howdoyouwanttouploadsuppressedcontacts')}</Typography>
                                </StepTitle>
                                <StepContent isMobile={isMobile}>
                                    <Radio
                                        name="uploadMethod"
                                        required={true}
                                        defaultValue={UploadType.Upload}
                                        onChange={this.onUploadChange.bind(this)}
                                        radios={[
                                            {
                                                label: t('uploadfilecontacts'),
                                                value: 'upload',
                                            },
                                            {
                                                label: t('uploadtypecontacts'),
                                                value: 'manual',
                                            },
                                        ]}
                                    />
                                </StepContent>
                            </SingleStep>
                        ) : (
                            ''
                        )}
                        <SingleStep>
                            <StepTitle>
                                <NumberedItem>{isMobile ? '1' : '2'}</NumberedItem>
                                <Typography variant="h2">{t('addsuppressions')}</Typography>
                            </StepTitle>
                            {!isMobile ? (
                                <StepContent>{this.renderUploadStep()}</StepContent>
                            ) : (
                                <StepContent isMobile={isMobile}>
                                    <TextArea
                                        id="addSuppressionTextArea"
                                        name="addSuppressionTextArea"
                                        label={t('uploadtypecontacts')}
                                        inputRef={this.textAreaRef}
                                        placeholder={'email1, email2, ...'}
                                        rows={11}
                                    />
                                </StepContent>
                            )}
                        </SingleStep>
                        <SingleStep>
                            <StepTitle>
                                <NumberedItem>{isMobile ? '2' : '3'}</NumberedItem>
                                <Typography variant="h2">{t('suppressionsstatuses')}</Typography>
                            </StepTitle>
                            <StepContent isMobile={isMobile}>{this.renderContactStatusSelect()}</StepContent>
                        </SingleStep>
                    </Grid>
                    <AddContactGrid isMobile={isMobile} container alignItems="flex-start" justifyContent="flex-end" direction="row">
                        <PrimaryButton
                            text={t('addsuppressions')}
                            onClick={this.onAddContactClick.bind(this)}
                            disabled={this.checkIfUploadDisabled()}
                        />
                    </AddContactGrid>
                </StyledPaper>
            </Container>
        );
    }
}

export default compose(
    withRouter,
    connect<AddSuppressionInterface, DispatchProps>(mapState, dispatchProps),
    withTranslation(),
    withStyles(styles)
)(AddSuppression);
